import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usuarioService, Rol, UsuarioAdmin, CreateUsuarioDto, UpdateUsuarioDto } from '../services/usuarioService';
import { formatRut, validateNombre, limitRutLength } from '../utils/rut';
import { useTenantStore } from '../store/tenantStore';
import { useAuthStore } from '../store/authStore';

const Usuarios: React.FC = () => {
  const queryClient = useQueryClient();
  const { tenantId, empresaNombre } = useTenantStore();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  
  // Carga de usuarios según rol y contexto
  const { data: usuarios = [], isLoading, error } = useQuery({ 
    queryKey: ['usuarios', tenantId, isSuperAdmin], 
    queryFn: async () => {
      if (tenantId && isSuperAdmin) {
        // SuperAdmin gestionando una empresa específica
        return await usuarioService.getUsuariosEmpresa(tenantId);
      }
      if (tenantId && !isSuperAdmin) {
        // Admin dentro de su tenant: listar miembros
        return await usuarioService.getMiembrosEmpresa();
      }
      // Sin tenantId (vista global) o superadmin sin selección: fallback auth
      return await usuarioService.getUsuariosAuth();
    },
    enabled: true
  });

  const [search, setSearch] = useState('');
  const [rolFilter, setRolFilter] = useState<'todos' | Rol>('todos');
  const [soloActivos, setSoloActivos] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<UsuarioAdmin | null>(null);

  const [form, setForm] = useState<CreateUsuarioDto | UpdateUsuarioDto>({ rol: 'Usuario', activo: true } as any);
  const [formError, setFormError] = useState<string | null>(null);

  // Crear usuario según rol
  const createMut = useMutation({
    mutationFn: async (dto: CreateUsuarioDto) => {
      if (tenantId && isSuperAdmin) {
        // Crear usuario directo en empresa (endpoint SuperAdmin)
        return await usuarioService.createUsuarioEmpresa(tenantId, dto);
      }
      if (tenantId && !isSuperAdmin) {
        // Admin: crear usuario global y vincular como miembro
        const created = await usuarioService.createUsuarioAuth(dto);
        await usuarioService.addMiembroEmpresa(created.id);
        return created;
      }
      // Sin tenant: crear global
      return await usuarioService.createUsuarioAuth(dto);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios', tenantId, isSuperAdmin] }),
  });

  // Actualizar usuario (para Admin y SuperAdmin usamos /auth cuando no hay endpoint específico por empresa)
  const updateMut = useMutation({
    mutationFn: async ({ id, dto }: { id: number; dto: UpdateUsuarioDto }) => {
      if (tenantId && isSuperAdmin) {
        return await usuarioService.updateUsuarioEmpresa(tenantId, id, dto);
      }
      return await usuarioService.updateUsuarioAuth(id, dto);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios', tenantId, isSuperAdmin] }),
  });

  // Eliminar usuario o desvincular miembro
  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      if (tenantId && isSuperAdmin) {
        await usuarioService.deleteUsuarioEmpresa(tenantId, id);
        return;
      }
      if (tenantId && !isSuperAdmin) {
        await usuarioService.removeMiembroEmpresa(id);
        return;
      }
      await usuarioService.deleteUsuarioAuth(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios', tenantId, isSuperAdmin] }),
  });

  const filtered = useMemo(() => {
    return usuarios.filter((u: any) => {
      const matchesSearch = (u.nombre || '').toLowerCase().includes(search.toLowerCase())
        || (u.apellido || '').toLowerCase().includes(search.toLowerCase())
        || (u.email || '').toLowerCase().includes(search.toLowerCase())
        || (u.rut || '').toLowerCase().includes(search.toLowerCase());
      const matchesRol = rolFilter === 'todos' || u.rol === rolFilter;
      const matchesActivo = !soloActivos || u.activo;
      return matchesSearch && matchesRol && matchesActivo;
    });
  }, [usuarios, search, rolFilter, soloActivos]);

  const openCreate = () => {
    setEditing(null);
    setForm({ rol: 'Usuario' });
    setShowForm(true);
  };

  const openEdit = (u: UsuarioAdmin) => {
    setEditing(u);
    setForm({
      nombre: u.nombre,
      apellido: u.apellido,
      email: u.email || undefined,
      rut: u.rut || undefined,
      rol: (u.rol as Rol) || 'Usuario',
      activo: u.activo,
    } as UpdateUsuarioDto);
    setShowForm(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const f: any = form;
    // Validación client-side acorde a reglas backend
    const { validateRut, validateNombre } = await import('../utils/rut');
    const errores: string[] = [];
    if ((f.rol as Rol) === 'Admin') {
      if (!f.nombre || !f.apellido || !f.email || (!editing && !f.password)) errores.push('Admin requiere nombre, apellido, email y contraseña.');
      if (f.nombre && !validateNombre(f.nombre)) errores.push('Nombre solo puede contener letras y espacios.');
      if (f.apellido && !validateNombre(f.apellido)) errores.push('Apellido solo puede contener letras y espacios.');
    } else if ((f.rol as Rol) === 'Usuario') {
      if (!f.nombre || !f.rut) errores.push('Usuario (personal de aseo) requiere nombre y RUT.');
      if (f.nombre && !validateNombre(f.nombre)) errores.push('Nombre solo puede contener letras y espacios.');
      if (f.rut && !validateRut(f.rut)) errores.push('RUT inválido.');
    }
    if (errores.length) { setFormError(errores.join(' ')); return; }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, dto: form as UpdateUsuarioDto });
      } else {
        await createMut.mutateAsync(form as CreateUsuarioDto);
      }
      setShowForm(false);
    } catch (err: any) {
      // Manejar específicamente el error de límite de plan
      if (err?.response?.data?.message?.includes('límite de usuarios')) {
        setFormError('Has alcanzado el límite de usuarios de tu plan. Considera subir de plan para crear más usuarios.');
      } else if (err?.response?.status === 403) {
        setFormError('No tienes permisos para esta acción.');
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Error al guardar usuario';
        setFormError(msg);
      }
    }
  };

  if (isLoading) return <div className="max-w-7xl mx-auto py-6 px-3">Cargando usuarios...</div>;
  if (error) return <div className="max-w-7xl mx-auto py-6 px-3 text-red-600">Error al cargar usuarios</div>;

  return (
    <div className="max-w-7xl mx-auto py-6 px-3 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-sm text-gray-600">
            {tenantId && empresaNombre 
              ? `Administra usuarios de ${empresaNombre}`
              : 'Administra usuarios del sistema (roles: Admin, Supervisor, Usuario).'
            }
          </p>
          {tenantId && empresaNombre && (
            <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Empresa: {empresaNombre}
            </div>
          )}
        </div>
        <button onClick={openCreate} className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700">Nuevo Usuario</button>
      </div>

      <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, email o RUT" className="border rounded px-3 py-2 text-sm" />
          <select value={rolFilter} onChange={(e) => setRolFilter(e.target.value as any)} className="border rounded px-3 py-2 text-sm">
            <option value="todos">Todos los roles</option>
            <option value="Admin">Admin</option>
            <option value="Usuario">Usuario</option>
          </select>
          <label className="inline-flex items-center text-sm">
            <input type="checkbox" checked={soloActivos} onChange={(e) => setSoloActivos(e.target.checked)} className="mr-2" /> Solo activos
          </label>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">RUT</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((u: any) => (
                <tr key={u.id}>
                  <td className="px-4 py-3">{u.id}</td>
                  <td className="px-4 py-3">{[u.nombre, u.apellido].filter(Boolean).join(' ') || '-'}</td>
                  <td className="px-4 py-3">{u.email || '-'}</td>
                  <td className="px-4 py-3">{u.rut ? formatRut(u.rut) : '-'}</td>
                  <td className="px-4 py-3">{u.rol}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${u.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{u.activo ? 'Activo' : 'Inactivo'}</span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <button onClick={() => openEdit(u)} className="text-indigo-600 hover:text-indigo-900 text-xs">Editar</button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) return;
                        try {
                          await deleteMut.mutateAsync(u.id);
                        } catch (err: any) {
                          alert(err?.response?.data?.message || 'Error al eliminar usuario');
                        }
                      }}
                      className="text-red-600 hover:text-red-900 text-xs"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>No hay usuarios</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow w-full max-w-lg p-4">
            <h3 className="text-lg font-semibold mb-3">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
            {formError && <div className="mb-3 text-red-600 bg-red-50 rounded p-2 text-sm">{formError}</div>}
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre</label>
                  <input value={(form as any).nombre || ''} onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))} className={`w-full border rounded px-3 py-2 text-sm ${((form as any).nombre && !validateNombre((form as any).nombre)) ? 'border-red-500' : ''}`} />
                  {((form as any).nombre && !validateNombre((form as any).nombre)) && (
                    <div className="text-xs text-red-600 mt-1">Solo letras y espacios</div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Apellido</label>
                  <input value={(form as any).apellido || ''} onChange={(e) => setForm(prev => ({ ...prev, apellido: e.target.value }))} className={`w-full border rounded px-3 py-2 text-sm ${((form as any).apellido && !validateNombre((form as any).apellido)) ? 'border-red-500' : ''}`} />
                  {((form as any).apellido && !validateNombre((form as any).apellido)) && (
                    <div className="text-xs text-red-600 mt-1">Solo letras y espacios</div>
                  )}
                </div>
                {(form as any).rol === 'Admin' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email (Admin)</label>
                      <input type="email" value={(form as any).email || ''} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
                    </div>
                    {!editing && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Password (Admin)</label>
                        <input type="password" value={(form as any).password || ''} onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                    )}
                  </>
                )}
                {(form as any).rol === 'Usuario' && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">RUT (Usuario - personal de aseo)</label>
                    <input
                      value={formatRut((form as any).rut || '')}
                      onChange={(e) => {
                        // Limitar ingreso a 9 dígitos de cuerpo + 1 DV (0-9 o K)
                        const raw = limitRutLength(e.target.value);
                        setForm(prev => ({ ...prev, rut: raw }));
                      }}
                      className="w-full border rounded px-3 py-2 text-sm"
                      inputMode="numeric"
                      placeholder="20.657.720-7"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rol</label>
                  <select value={(form as any).rol} onChange={(e) => setForm(prev => ({ ...prev, rol: e.target.value as any }))} className="w-full border rounded px-3 py-2 text-sm">
                    <option value="Admin">Admin</option>
                    <option value="Usuario">Usuario</option>
                  </select>
                </div>
                {editing && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Activo</label>
                    <select value={(form as any).activo ? 'true' : 'false'} onChange={(e) => setForm(prev => ({ ...(prev as any), activo: e.target.value === 'true' }))} className="w-full border rounded px-3 py-2 text-sm">
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-md bg-gray-100">Cancelar</button>
                <button type="submit" disabled={createMut.isPending || updateMut.isPending} className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white disabled:opacity-50">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;


